import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Icon,
  Progress,
  Badge,
  Divider,
  CircularProgress,
  CircularProgressLabel,
} from "@chakra-ui/react";
import { FaHome, FaDollarSign, FaPercent, FaCalculator } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { LeadCaptureForm } from "../../../components/calculator/LeadCaptureForm";
import { CalculatorType } from "../../../generated/graphql";

const BorrowingPowerCalculator: React.FC = () => {
  const [inputs, setInputs] = useState({
    annualIncome: 80000,
    otherIncome: 0,
    monthlyExpenses: 2000,
    debts: 10000,
    dependants: 0,
    deposit: 100000,
  });

  const [results, setResults] = useState<any>(null);

  const handleInputChange = (field: string, value: any) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const calculate = () => {
    const totalIncome = inputs.annualIncome + inputs.otherIncome;
    const monthlyIncome = totalIncome / 12;

    // Serviceability calculation (simplified)
    // Banks typically use 30% debt service ratio
    const maxMonthlyPayment = monthlyIncome * 0.3 - inputs.monthlyExpenses * 0.3;

    // Calculate max loan based on 6% assessment rate, 30 year term
    const assessmentRate = 0.06 / 12;
    const months = 30 * 12;
    const maxLoan =
      (maxMonthlyPayment * (Math.pow(1 + assessmentRate, months) - 1)) /
      (assessmentRate * Math.pow(1 + assessmentRate, months));

    // Adjust for existing debts
    const debtImpact = inputs.debts * 0.3; // Simplified debt impact
    const adjustedMaxLoan = Math.max(0, maxLoan - debtImpact);

    // Monthly repayment at 5.5% actual rate
    const actualRate = 0.055 / 12;
    const monthlyRepayment =
      (adjustedMaxLoan * actualRate * Math.pow(1 + actualRate, months)) /
      (Math.pow(1 + actualRate, months) - 1);

    // Property value = loan + deposit
    const propertyValue = adjustedMaxLoan + inputs.deposit;

    // LVR calculation
    const lvr = adjustedMaxLoan > 0 ? (adjustedMaxLoan / propertyValue) * 100 : 0;

    setResults({
      maxBorrowing: adjustedMaxLoan.toFixed(0),
      monthlyRepayment: monthlyRepayment.toFixed(0),
      propertyValue: propertyValue.toFixed(0),
      lvr: lvr.toFixed(1),
      deposit: inputs.deposit,
    });
  };

  const reset = () => {
    setInputs({
      annualIncome: 80000,
      otherIncome: 0,
      monthlyExpenses: 2000,
      debts: 10000,
      dependants: 0,
      deposit: 100000,
    });
    setResults(null);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/calculators/borrowing-power"
        fallbackTitle="Borrowing Power Calculator"
        fallbackDescription="Calculate how much you can borrow for a home loan based on your income and expenses"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-r, blue.600, cyan.400)"
        color="white"
        py={16}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={4} textAlign="center">
            <Icon as={FaCalculator} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Borrowing Power Calculator
            </Heading>
            <Text fontSize="xl" maxW="2xl">
              Discover how much you can borrow based on your financial situation
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Vertical Progressive Form Layout */}
      <Container maxW="container.md" py={12}>
        <VStack spacing={8} align="stretch">
          {/* Section 1: Income */}
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack>
                  <Icon as={FaDollarSign} color="blue.500" boxSize={6} />
                  <Heading size="md" color="blue.600">
                    Your Income
                  </Heading>
                </HStack>
                <Divider />

                <FormControl>
                  <FormLabel>Annual Income (before tax)</FormLabel>
                  <Input
                    type="number"
                    size="lg"
                    value={inputs.annualIncome}
                    onChange={(e) =>
                      handleInputChange("annualIncome", parseFloat(e.target.value) || 0)
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Your primary employment income
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Other Annual Income (optional)</FormLabel>
                  <Input
                    type="number"
                    size="lg"
                    value={inputs.otherIncome}
                    onChange={(e) =>
                      handleInputChange("otherIncome", parseFloat(e.target.value) || 0)
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Rental income, investments, bonuses (banks typically discount these by 20%)
                  </Text>
                </FormControl>

                <Box bg="blue.50" p={4} borderRadius="md">
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="semibold">Total Annual Income:</Text>
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">
                      ${(inputs.annualIncome + inputs.otherIncome).toLocaleString()}
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Section 2: Expenses & Commitments */}
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack>
                  <Icon as={FaHome} color="red.500" boxSize={6} />
                  <Heading size="md" color="red.600">
                    Expenses & Commitments
                  </Heading>
                </HStack>
                <Divider />

                <FormControl>
                  <FormLabel>Monthly Living Expenses</FormLabel>
                  <Input
                    type="number"
                    size="lg"
                    value={inputs.monthlyExpenses}
                    onChange={(e) =>
                      handleInputChange("monthlyExpenses", parseFloat(e.target.value) || 0)
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Groceries, utilities, transport, insurance, entertainment
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Existing Debts</FormLabel>
                  <Input
                    type="number"
                    size="lg"
                    value={inputs.debts}
                    onChange={(e) => handleInputChange("debts", parseFloat(e.target.value) || 0)}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Credit cards, personal loans, car loans (total balance)
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Number of Dependants</FormLabel>
                  <Input
                    type="number"
                    size="lg"
                    value={inputs.dependants}
                    onChange={(e) => handleInputChange("dependants", parseFloat(e.target.value) || 0)}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Children or other financial dependants
                  </Text>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Section 3: Deposit */}
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack>
                  <Icon as={FaPercent} color="green.500" boxSize={6} />
                  <Heading size="md" color="green.600">
                    Your Deposit
                  </Heading>
                </HStack>
                <Divider />

                <FormControl>
                  <FormLabel>Deposit Amount</FormLabel>
                  <Input
                    type="number"
                    size="lg"
                    value={inputs.deposit}
                    onChange={(e) => handleInputChange("deposit", parseFloat(e.target.value) || 0)}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Savings available for deposit (aim for 20% to avoid LMI)
                  </Text>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Calculate Button */}
          <HStack spacing={4}>
            <Button colorScheme="blue" size="lg" onClick={calculate} flex={1} height="60px">
              Calculate Borrowing Power
            </Button>
            <Button variant="outline" size="lg" onClick={reset} height="60px">
              Reset
            </Button>
          </HStack>

          {/* Results Section */}
          {results && (
            <Card bg="blue.50" borderWidth={2} borderColor="blue.400">
              <CardBody>
                <VStack spacing={6} align="stretch">
                  <HStack justify="space-between">
                    <Heading size="lg" color="blue.700">
                      Your Borrowing Power
                    </Heading>
                    <Badge colorScheme="blue" fontSize="md" px={4} py={2}>
                      Estimated
                    </Badge>
                  </HStack>

                  <Divider />

                  <Box textAlign="center" py={4}>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Maximum Borrowing Capacity
                    </Text>
                    <Heading size="3xl" color="blue.600">
                      ${parseInt(results.maxBorrowing).toLocaleString()}
                    </Heading>
                  </Box>

                  <Divider />

                  <HStack spacing={8} justify="center">
                    <VStack>
                      <CircularProgress
                        value={parseFloat(results.lvr)}
                        size="120px"
                        thickness="12px"
                        color={parseFloat(results.lvr) > 80 ? "orange.400" : "green.400"}
                      >
                        <CircularProgressLabel fontSize="xl" fontWeight="bold">
                          {results.lvr}%
                        </CircularProgressLabel>
                      </CircularProgress>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        LVR (Loan to Value)
                      </Text>
                      {parseFloat(results.lvr) > 80 && (
                        <Text fontSize="xs" color="orange.600" textAlign="center" maxW="120px">
                          LMI required
                        </Text>
                      )}
                    </VStack>

                    <VStack align="stretch" flex={1}>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Monthly Repayment:
                        </Text>
                        <Text fontWeight="bold" fontSize="lg">
                          ${parseInt(results.monthlyRepayment).toLocaleString()}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Your Deposit:
                        </Text>
                        <Text fontWeight="bold" fontSize="lg" color="green.600">
                          ${parseInt(results.deposit).toLocaleString()}
                        </Text>
                      </HStack>
                      <Divider />
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.700" fontWeight="semibold">
                          Property Value:
                        </Text>
                        <Text fontWeight="bold" fontSize="xl" color="blue.600">
                          ${parseInt(results.propertyValue).toLocaleString()}
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>

                  <Box bg="white" p={4} borderRadius="md">
                    <Text fontSize="xs" color="gray.600" textAlign="center">
                      Based on 30-year loan term at 5.5% interest rate (actual rate). Banks assess at
                      higher rates (~6-7%) for serviceability. This is an estimate only - actual
                      borrowing capacity varies by lender and individual circumstances.
                    </Text>
                  </Box>

                  <LeadCaptureForm
                    calculatorType={CalculatorType.BorrowingPower}
                    calculatorName="Borrowing Power Calculator"
                    calculatorPath="/calculators/borrowing-power"
                    inputData={inputs}
                    resultData={results}
                    hasResults={!!results}
                    accentColor="blue"
                  />
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default BorrowingPowerCalculator;
